import { useState } from "react";
import { motion, useMotionValueEvent, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Wifi, BatteryFull, Mail, Phone } from "lucide-react";
import { useLeadDialog } from "@/components/site/LeadDialog";
import { PORTFOLIO, CONTACT, LOGO_URL } from "@/lib/constants";
import { useDrivenStyle, mapStops } from "./timeline";

/**
 * WalshOS - the fictional Website Walsh desktop that fills the viewport once the
 * camera is "inside" the laptop screen. Pure DOM/CSS/Framer Motion layered over
 * the WebGL canvas; wallpaper visuals deliberately match the 3D screen texture.
 *
 * Scroll timeline (progress p):
 *  0.455–0.495  fade in over the canvas
 *  0.47–0.585   cinematic cursor enters → hovers → clicks OUR WORK
 *  0.578        file browser opens
 *  0.62–0.685   cursor → first project, clicks
 *  0.685–0.85   six project windows cycle
 *  0.85–0.925   CTA, then fade out as camera pulls back
 */

const EASE = [0.22, 1, 0.36, 1];

function CursorArrow({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none">
      <path
        d="M4 2 L4 18.5 L8.2 14.8 L11 21 L13.6 19.6 L10.9 13.4 L16.5 13.4 Z"
        fill="#0f172a"
        stroke="#ffffff"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FolderGlyph({ active }) {
  return (
    <svg viewBox="0 0 56 44" className="w-14 h-11">
      <defs>
        <linearGradient id="ww-folder" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#2dd4bf" />
          <stop offset="1" stopColor="#10b981" />
        </linearGradient>
      </defs>
      <path
        d="M3 8 a4 4 0 0 1 4-4 h12 l5 5 h25 a4 4 0 0 1 4 4 v24 a4 4 0 0 1-4 4 H7 a4 4 0 0 1-4-4 Z"
        fill="url(#ww-folder)"
        opacity={active ? 1 : 0.9}
      />
      <path
        d="M3 14 h50 v23 a4 4 0 0 1-4 4 H7 a4 4 0 0 1-4-4 Z"
        fill="#0f2a26"
        opacity="0.28"
      />
    </svg>
  );
}

function DesktopIcon({ id, label, active, children, testid }) {
  return (
    <div
      data-testid={testid}
      data-active={active || undefined}
      className={`flex flex-col items-center gap-2 w-24 px-2 py-3 rounded-2xl border transition-all duration-300 ${
        active
          ? "bg-white/[0.08] border-teal-300/40 shadow-[0_0_30px_rgba(45,212,191,0.25)] scale-[1.04]"
          : "bg-transparent border-transparent"
      }`}
    >
      {children}
      <span className="text-[11px] font-medium tracking-wide text-white/85 font-body">
        {label}
      </span>
    </div>
  );
}

function WindowChrome({ title, dimmed }) {
  return (
    <div className="flex items-center gap-3 px-4 h-11 border-b border-white/[0.07] bg-white/[0.03]">
      <div className="flex gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
        <span className="w-2.5 h-2.5 rounded-full bg-teal-400/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
      </div>
      <div className="flex-1 flex justify-center">
        <span
          className={`px-4 py-1 rounded-md bg-black/40 border border-white/[0.06] text-[11px] font-mono tracking-wide truncate max-w-[280px] ${
            dimmed ? "text-white/40" : "text-white/70"
          }`}
        >
          {title}
        </span>
      </div>
      <span className="w-10" />
    </div>
  );
}

function ProjectWindow({ project, offset }) {
  // offset 0 = front, -1 = one back, -2 = two back
  const styles = [
    { x: "0%", y: "0%", scale: 1, opacity: 1, zIndex: 30, filter: "none" },
    { x: "-5%", y: "4%", scale: 0.94, opacity: 0.5, zIndex: 20, filter: "blur(1px)" },
    { x: "-10%", y: "8%", scale: 0.88, opacity: 0.25, zIndex: 10, filter: "blur(2px)" },
  ];
  const s = styles[Math.min(2, -offset)];
  const isFront = offset === 0;
  return (
    <div
      style={{ zIndex: s.zIndex, pointerEvents: isFront ? "auto" : "none" }}
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(900px,66vw)]"
    >
      <motion.div
        key={project.slug}
        data-testid={`project-window-${project.slug}`}
        initial={{ opacity: 0, scale: 0.9, y: "6%", x: "4%" }}
        animate={{ opacity: s.opacity, scale: s.scale, y: s.y, x: s.x, filter: s.filter }}
        exit={{ opacity: 0, scale: 0.9, y: "6%", x: "-6%" }}
        transition={{ duration: 0.55, ease: EASE }}
        className="rounded-2xl overflow-hidden border border-white/10 bg-[#101215]/95 backdrop-blur-xl shadow-[0_40px_120px_rgba(0,0,0,0.6)]"
      >
      <WindowChrome title={project.href.replace("https://", "")} />
      <div className="relative">
        <div className="relative h-[34vh] min-h-[220px] overflow-hidden">
          <img
            src={project.screenshot}
            alt={project.name}
            className="w-full h-full object-cover object-top"
            draggable="false"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#101215] via-transparent to-transparent" />
        </div>
        <div className="flex items-end justify-between gap-6 px-6 pb-6 -mt-10 relative">
          <div>
            <p className="text-[10px] uppercase tracking-[0.26em] text-teal-300/90 font-semibold font-body">
              {project.label}
            </p>
            <h3 className="mt-1.5 font-display font-bold text-2xl md:text-3xl tracking-tight text-white">
              {project.name}
            </h3>
          </div>
          <a
            href={project.href}
            target="_blank"
            rel="noreferrer"
            data-testid={`visit-link-${project.slug}`}
            tabIndex={isFront ? 0 : -1}
            className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-gradient-to-r from-teal-400 to-emerald-400 pl-4 pr-2 py-1.5 text-[13px] font-semibold text-black transition-transform hover:scale-[1.03]"
          >
            Visit Website
            <span className="inline-grid place-items-center w-6 h-6 rounded-full bg-black/15">
              <ArrowUpRight className="w-3.5 h-3.5" />
            </span>
          </a>
        </div>
      </div>
      </motion.div>
    </div>
  );
}

function FileBrowser({ pushedBack }) {
  return (
    <div
      style={{ pointerEvents: "none", zIndex: 5 }}
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(880px,64vw)]"
    >
      <motion.div
        data-testid="our-work-browser"
        initial={{ opacity: 0, scale: 0.86, y: 30 }}
        animate={
          pushedBack
            ? { opacity: 0.45, scale: 0.9, x: "12%", y: "7%", filter: "blur(1.5px)" }
            : { opacity: 1, scale: 1, x: "0%", y: "0%", filter: "blur(0px)" }
        }
        exit={{ opacity: 0, scale: 0.85, y: 60 }}
        transition={{ duration: 0.6, ease: EASE }}
        className="rounded-2xl overflow-hidden border border-white/10 bg-[#0e1013]/90 backdrop-blur-xl shadow-[0_40px_120px_rgba(0,0,0,0.55)]"
      >
      <WindowChrome title="walsh os - our work" />
      <div className="flex">
        <div className="hidden sm:block w-40 border-r border-white/[0.06] p-4 space-y-2">
          {["Recents", "Our Work", "Client Sites", "Archive"].map((f, i) => (
            <div
              key={f}
              className={`text-[11px] font-body px-2.5 py-1.5 rounded-md ${
                i === 1
                  ? "bg-teal-400/10 text-teal-200 border border-teal-300/20"
                  : "text-white/45"
              }`}
            >
              {f}
            </div>
          ))}
        </div>
        <div className="flex-1 p-5">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-display font-bold text-white text-lg tracking-tight">
              Our Work
            </h3>
            <span className="text-[10px] font-mono text-white/35">
              6 projects
            </span>
          </div>
          <div className="grid grid-cols-3 gap-3.5">
            {PORTFOLIO.map((p, i) => (
              <div
                key={p.slug}
                data-testid={`work-file-${p.slug}`}
                className={`group rounded-xl border overflow-hidden transition-all duration-300 ${
                  i === 0
                    ? "border-teal-300/40 shadow-[0_0_24px_rgba(45,212,191,0.18)]"
                    : "border-white/[0.08]"
                } bg-white/[0.03]`}
              >
                <div className="h-[7.5vh] min-h-[54px] overflow-hidden">
                  <img
                    src={p.screenshot}
                    alt=""
                    className="w-full h-full object-cover object-top opacity-90"
                    draggable="false"
                  />
                </div>
                <div className="px-2.5 py-2">
                  <p className="text-[10.5px] font-semibold text-white/85 truncate font-body">
                    {p.name}
                  </p>
                  <p className="text-[9px] text-white/35 truncate font-mono">
                    {p.href.replace("https://", "")}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      </motion.div>
    </div>
  );
}

function PortfolioOverview() {
  const { openPreview } = useLeadDialog();
  return (
    <motion.div
      data-testid="portfolio-overview"
      initial={{ opacity: 0, y: 46, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.98 }}
      transition={{ duration: 0.65, ease: EASE }}
      className="absolute inset-0 z-40 flex flex-col items-center justify-center px-6 md:px-14"
    >
      <p className="text-[11px] uppercase tracking-[0.3em] text-teal-300/80 font-semibold font-body">
        Our work: explore it yourself
      </p>

      {/* Horizontal project browser */}
      <div
        data-testid="portfolio-overview-strip"
        style={{ pointerEvents: "auto" }}
        className="mt-6 w-full max-w-6xl overflow-x-auto no-scrollbar snap-x snap-mandatory"
      >
        <div className="flex gap-4 w-max mx-auto px-1 py-1">
          {PORTFOLIO.map((p) => (
            <a
              key={p.slug}
              href={p.href}
              target="_blank"
              rel="noreferrer"
              data-testid={`overview-project-${p.slug}`}
              className="group snap-center shrink-0 w-[168px] md:w-[196px] rounded-xl overflow-hidden border border-white/10 bg-white/[0.04] backdrop-blur-md transition-all duration-300 hover:border-teal-300/50 hover:shadow-[0_0_36px_rgba(45,212,191,0.22)] hover:-translate-y-1"
            >
              <div className="h-[96px] md:h-[112px] overflow-hidden">
                <img
                  src={p.screenshot}
                  alt={p.name}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.05]"
                  draggable="false"
                />
              </div>
              <div className="px-3 py-2.5 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-white/90 truncate font-body">
                    {p.name}
                  </p>
                  <p className="text-[9px] text-white/35 truncate font-mono">
                    {p.href.replace("https://", "")}
                  </p>
                </div>
                <ArrowUpRight className="w-3.5 h-3.5 shrink-0 text-teal-300/70 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* Prominent CTA */}
      <div className="mt-9 text-center">
        <p className="text-[11px] uppercase tracking-[0.3em] text-white/40 font-semibold font-body">
          Like what you see?
        </p>
        <h2 className="mt-3 font-display font-bold uppercase text-white text-3xl md:text-5xl leading-[0.96] tracking-tight">
          Your business
          <span className="text-brand-gradient-bright"> could be next.</span>
        </h2>
        <p className="mt-4 text-white/50 max-w-md mx-auto text-sm leading-relaxed font-body">
          See what your new website could look like before you pay anything.
        </p>
        <button
          type="button"
          onClick={() => openPreview()}
          data-testid="os-cta-free-preview"
          style={{ pointerEvents: "auto" }}
          className="mt-7 group inline-flex items-center gap-3 pl-7 pr-2.5 py-2.5 rounded-full bg-gradient-to-r from-teal-400 to-emerald-400 text-black text-base font-bold font-body transition-all duration-300 hover:scale-[1.05] hover:shadow-[0_0_44px_rgba(45,212,191,0.45)]"
        >
          Get Your Free Website Preview
          <span className="inline-grid place-items-center w-10 h-10 rounded-full bg-black/15 transition-transform duration-300 group-hover:rotate-45">
            <ArrowUpRight className="w-5 h-5" />
          </span>
        </button>
      </div>
    </motion.div>
  );
}

export default function WalshOS({ progress }) {
  const [browserOpen, setBrowserOpen] = useState(false);
  const [activeProject, setActiveProject] = useState(-1);
  const [overviewOn, setOverviewOn] = useState(false);
  const [cursorOn, setCursorOn] = useState(false);
  const [hoverTarget, setHoverTarget] = useState(null);

  useMotionValueEvent(progress, "change", (p) => {
    setBrowserOpen(p >= 0.578 && p < 0.83);
    const idx =
      p < 0.685 ? -1 : Math.min(PORTFOLIO.length - 1, Math.floor((p - 0.685) / 0.0242));
    setActiveProject(p >= 0.83 ? -1 : idx);
    setOverviewOn(p >= 0.83);
    setCursorOn(p > 0.465 && p < 0.85);
    setHoverTarget(
      p > 0.54 && p < 0.585
        ? "work"
        : p > 0.63 && p < 0.67
          ? "first-project"
          : null,
    );
  });

  const [osGone, setOsGone] = useState(true);
  useMotionValueEvent(progress, "change", (p) =>
    setOsGone(p < 0.445 || p > 0.955),
  );

  const osRef = useDrivenStyle(progress, (el, p) => {
    el.style.opacity = String(
      mapStops(p, [0.452, 0.495, 0.925, 0.95], [0, 1, 1, 0]),
    );
  });

  // Cinematic cursor path (percent of viewport) - driven imperatively
  const CURSOR_STOPS = [0.465, 0.52, 0.558, 0.585, 0.615, 0.648, 0.685, 0.8, 0.84];
  const CURSOR_X = [80, 60, 8.2, 8.2, 30, 42, 42, 74, 74];
  const CURSOR_Y = [86, 68, 19.5, 19.5, 38, 49, 49, 58, 58];
  const SCALE_STOPS = [0.465, 0.552, 0.56, 0.568, 0.642, 0.65, 0.658];
  const SCALE_VALS = [1, 1, 0.72, 1, 1, 0.72, 1];
  const RIPPLE_STOPS = [0.552, 0.56, 0.575, 0.642, 0.65, 0.665];
  const RIPPLE_VALS = [0, 0.9, 0, 0, 0.9, 0];

  const cursorRef = useDrivenStyle(progress, (el, p) => {
    const x = mapStops(p, CURSOR_STOPS, CURSOR_X);
    const y = mapStops(p, CURSOR_STOPS, CURSOR_Y);
    const s = mapStops(p, SCALE_STOPS, SCALE_VALS);
    el.style.left = `${x}vw`;
    el.style.top = `${y}vh`;
    el.style.transform = `scale(${s})`;
  });
  const rippleRef = useDrivenStyle(progress, (el, p) => {
    el.style.opacity = String(mapStops(p, RIPPLE_STOPS, RIPPLE_VALS));
  });

  return (
    <div
      ref={osRef}
      data-testid="walsh-os"
      style={{
        opacity: 0,
        pointerEvents: "none",
        visibility: osGone ? "hidden" : "visible",
      }}
      className="absolute inset-0 z-20 overflow-hidden bg-[#0b0d0f]"
    >
      {/* ---- Wallpaper (matches the WebGL screen texture) ---- */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 18% 20%, rgba(13,148,136,0.30), rgba(11,13,15,0) 50%), radial-gradient(circle at 85% 85%, rgba(16,185,129,0.20), rgba(11,13,15,0) 55%), radial-gradient(circle at 60% 5%, rgba(45,212,191,0.12), rgba(11,13,15,0) 30%), #0b0d0f",
          }}
        />
        <div className="absolute inset-0 opacity-100 [background-image:linear-gradient(to_right,rgba(255,255,255,0.035)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.035)_1px,transparent_1px)] [background-size:56px_56px]" />
        {/* Floating glass geometry */}
        <motion.div
          animate={{ y: [0, -18, 0], rotate: [0, 8, 0] }}
          transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
          className="absolute right-[12%] top-[18%] w-40 h-40 rounded-[2rem] border border-white/10 bg-white/[0.03] backdrop-blur-md"
        />
        <motion.div
          animate={{ y: [0, 14, 0], rotate: [45, 52, 45] }}
          transition={{ duration: 13, repeat: Infinity, ease: "easeInOut" }}
          className="absolute left-[16%] bottom-[20%] w-24 h-24 rounded-2xl border border-teal-300/20 bg-teal-400/[0.05] backdrop-blur-md"
        />
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          className="absolute right-[30%] bottom-[12%] w-14 h-14 rounded-full border border-emerald-300/25 bg-emerald-400/[0.06]"
        />
      </div>

      {/* ---- Top bar ---- */}
      <div className="absolute top-0 inset-x-0 h-[4.25vh] min-h-8 flex items-center justify-between px-6 bg-white/[0.05] border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <span className="w-4 h-4 rounded-[4px] overflow-hidden ring-1 ring-white/15">
            <img src={LOGO_URL} alt="" className="w-full h-full object-cover" />
          </span>
          <span className="font-display font-semibold text-[13px] text-white/90 tracking-tight">
            Website Walsh
          </span>
        </div>
        <div className="flex items-center gap-4 text-white/50">
          <Wifi className="w-3.5 h-3.5" />
          <BatteryFull className="w-4 h-4" />
          <span className="text-[12px] font-medium font-body">Walsh OS</span>
        </div>
      </div>

      {/* ---- Desktop icons ---- */}
      <div className="absolute left-[4%] top-[10%] flex flex-col gap-3">
        <DesktopIcon id="work" label="Our Work" active={hoverTarget === "work"} testid="os-icon-our-work">
          <FolderGlyph active={hoverTarget === "work"} />
        </DesktopIcon>
        <DesktopIcon id="studio" label="Walsh Studio" testid="os-icon-studio">
          <span className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#15181c] to-[#0b0d0f] border border-white/10 grid place-items-center overflow-hidden">
            <img src={LOGO_URL} alt="" className="w-9 h-9 object-cover rounded-md" />
          </span>
        </DesktopIcon>
        <DesktopIcon id="notes" label="Process.txt" testid="os-icon-notes">
          <span className="w-14 h-14 rounded-2xl bg-white/[0.04] border border-white/10 grid place-items-center">
            <span className="space-y-1.5">
              <span className="block w-7 h-0.5 bg-teal-300/70 rounded" />
              <span className="block w-5 h-0.5 bg-white/30 rounded" />
              <span className="block w-6 h-0.5 bg-white/30 rounded" />
            </span>
          </span>
        </DesktopIcon>
      </div>

      {/* ---- Dock ---- */}
      <div className="absolute bottom-[3.5%] left-1/2 -translate-x-1/2 flex items-center gap-2.5 px-4 py-2.5 rounded-2xl border border-white/10 bg-white/[0.05] backdrop-blur-xl">
        <span className="w-9 h-9 rounded-xl overflow-hidden ring-1 ring-white/15">
          <img src={LOGO_URL} alt="" className="w-full h-full object-cover" />
        </span>
        <span className="w-px h-6 bg-white/10" />
        <FolderGlyph active={false} />
        <a
          href={`mailto:${CONTACT.email}`}
          data-testid="os-dock-mail"
          style={{ pointerEvents: "auto" }}
          className="w-9 h-9 rounded-xl grid place-items-center bg-white/[0.06] border border-white/10 text-white/70 hover:text-teal-300 hover:border-teal-300/40 transition-colors"
        >
          <Mail className="w-4 h-4" />
        </a>
        <a
          href={`tel:${CONTACT.phoneRaw}`}
          data-testid="os-dock-phone"
          style={{ pointerEvents: "auto" }}
          className="w-9 h-9 rounded-xl grid place-items-center bg-white/[0.06] border border-white/10 text-white/70 hover:text-emerald-300 hover:border-emerald-300/40 transition-colors"
        >
          <Phone className="w-4 h-4" />
        </a>
      </div>

      {/* ---- File browser ---- */}
      <AnimatePresence>
        {browserOpen && <FileBrowser pushedBack={activeProject >= 0} />}
      </AnimatePresence>

      {/* ---- Project windows (active + two receding) ---- */}
      <AnimatePresence mode="popLayout">
        {activeProject >= 0 &&
          [-2, -1, 0].map((off) => {
            const i = activeProject + off;
            if (i < 0 || i >= PORTFOLIO.length) return null;
            return (
              <ProjectWindow
                key={PORTFOLIO[i].slug}
                project={PORTFOLIO[i]}
                offset={off}
              />
            );
          })}
      </AnimatePresence>

      {/* ---- Final interactive state: portfolio overview + prominent CTA ---- */}
      <AnimatePresence>
        {overviewOn && <PortfolioOverview />}
      </AnimatePresence>

      {/* ---- Cinematic cursor ---- */}
      {cursorOn && (
        <div
          ref={cursorRef}
          data-testid="os-cursor"
          className="absolute z-50 w-7 h-7 origin-top-left"
        >
          <span
            ref={rippleRef}
            className="absolute -inset-3 rounded-full border-2 border-teal-300 opacity-0"
          />
          <CursorArrow className="w-7 h-7 drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]" />
        </div>
      )}
    </div>
  );
}
